import * as basicLightbox from "basiclightbox";
import { callApsRenew, callApsUpdate, callAtsEnvelope } from "./fpd";
import {
	formAddCsrfProtection,
	getCookie,
	loadScript,
	plusTwitch,
	sendPublyticsEvent,
	setCookie,
	updateGAdataLayer,
	userNotifications,
	userLogout,
} from "./utils";

class LoginWindowHandler {
	constructor() {
		// The delegate must implement these methods:
		//  - authenticationSucceded()
		//  - authenticationFailedWithError(String error)
		this.delegate = undefined;

		// Used to test if login succeded on Login Windows close
		this.loginSucceded = false;

		// Lightbox
		this.loginLightbox = null;

		this.ready = false;

		this.anonymousClicks = new Array();

		this.anonymousFocuses = new Array();
	}

	async init() {
		return new Promise((resolve, reject) => {
			if (!document.querySelector("#login_registration_container")) {
				reject("Login window not found");
				return;
			}
			// Lightbox
			const btnLoginClose = document.querySelector(
				"#_close_login_registration_container",
			);
			this.loginLightbox = basicLightbox.create(
				document.querySelector("#login_registration_container"),
				{
					closable: false,
					onShow: (instance) => {
						this.loginSucceded = false;
						const lightbox = instance.element();
						document.body.addEventListener("keydown", (event) => {
							if (event.key === "Enter") {
								if (
									lightbox.querySelector("#login_window input.username")
										.value &&
									lightbox.querySelector("#login_window input.password").value
								) {
									lightbox
										.querySelector("#login_window #_login_submit")
										.click();
								}
							}
						});
						btnLoginClose.addEventListener("click", (event) => {
							event.preventDefault();
							instance.close();
						});
					},
					onClose: (instance) => {
						const login_window = instance
							.element()
							.querySelector("#login_window");
						login_window.querySelector("input.username").value = "";
						login_window.querySelector("input.password").value = "";
						login_window.querySelector(".error_list").classList.add("d-none");
						if (!this.loginSucceded) {
							this.delegate.authenticationFailedWithError(
								"Form chiuso senza login",
							);
						}
					},
				},
			);
			this.ready = true;
			resolve(this.ready);
		});
	}

	showLoginWindow() {
		this.loginLightbox.show(() => {
			this.loginLightbox
				.element()
				.querySelector(
					".basicLightbox__placeholder > #login_window input.username",
				)
				.focus();
		});
	}

	openLoginWindow() {
		// DEBUG ONLY
		console.log(" --- openLoginWindow() called");

		if (!this.ready) {
			this.init().then((is_ready) => {
				if (!is_ready) {
					return;
				}
				console.log(" --- LoginWindowHandler ready");
				this.showLoginWindow();
			});
		} else {
			this.showLoginWindow();
		}
	}

	removeEventHandlers() {
		console.log(" --- removeEventHandlers() called");
		for (const fn of this.anonymousClicks) {
			fn();
		}
		this.anonymousClicks = [];
		for (const fn of this.anonymousFocuses) {
			fn();
		}
		this.anonymousFocuses = [];
	}

	getUserData(callback) {
		// Retrieving user navigation data
		const userNavLoad = async () => {
			const nav = document.querySelector("#user_nav");
			if (nav) {
				await fetch(nav.dataset.url, {
					headers: {
						"X-Requested-With": "XMLHttpRequest",
					},
					cache: "no-cache",
				})
					.then((response) => response.json())
					.then((data) => {
						nav.innerHTML = data.html;
						const userNavReady = new Event("usernavready");
						document.dispatchEvent(userNavReady);

						// GA4
						updateGAdataLayer("datalayer_update", "user", {
							status: data.user_status,
						});
						sendPublyticsEvent("user", { user_status: data.user_status });
                        
            // Setup logout form
						userLogout();

						// event listeners for user notifications
						userNotifications();

						// FPD calls
						callAtsEnvelope(data.identifier);
						if (getCookie(FPD_COOKIE)) {
							callApsUpdate(data.identifier);
							setCookie(FPD_COOKIE, "", 0);
						} else {
							callApsRenew(data.identifier);
						}

						// setup Twitch Plus button
						plusTwitch(data.user_status);
					})
					.catch((error) => {
						console.warn(" --- Error retrieving user_nav data:  ", error);
					});
			}
		};

		// Retrieve bookmarks
		const retrieveBookmarksDataFromBookmark = async (bookmark) => {
			if (bookmark) {
				const bookmarkForm = bookmark.querySelector("form");
				const contentTypeId =
					bookmarkForm.querySelector("input[name=model]").value;
				const objectId = bookmarkForm.querySelector(
					"input[name=object_id]",
				).value;

				if (!contentTypeId || !objectId) {
					return;
				}

				const url = new URL(
					`/extra_bookmarks/${contentTypeId}/${objectId}/`,
					window.location.origin,
				);

				await fetch(url, {
					headers: {
						"X-Requested-With": "XMLHttpRequest",
					},
					cache: "no-cache",
				})
					.then((response) => response.json())
					.then((data) => {
						// favourited
						if (data.indexOf("favourited") >= 0) {
							if (contentTypeId === "auth.user") {
								// redattore
								if (document.querySelector("#tab_follow_staff")) {
									document
										.querySelector("#tab_follow_staff")
										.classList.add("active");
									document
										.querySelector("#tab_follow_staff")
										.querySelector(".tab_label").innerText = "Non seguire";
								}
							}
						}

						// subscribed
						if (data.indexOf("subscribed") >= 0) {
							if (contentTypeId === "article.article") {
								// leggi dopo
								if (document.querySelector("#_article_read_later")) {
									document
										.querySelector("#_article_read_later")
										.classList.add("active");
									document
										.querySelector("#_article_read_later")
										.querySelector(".tab_label").innerHTML = "Articolo salvato";
								}
							}

							if (contentTypeId === "gamecard.mergedgamecard") {
								// correlato articolo
								if (document.querySelector("#_article_related_follow")) {
									document
										.querySelector("#_article_related_follow")
										.classList.add("active");
									document
										.querySelector("#_article_related_follow")
										.querySelector(".tab_label").innerText = "Non seguire";
								}

								// scheda gioco
								if (document.querySelector("#_gamecard_follow")) {
									document
										.querySelector("#_gamecard_follow")
										.classList.add("active");
									document
										.querySelector("#_gamecard_follow .tab_label")
										.querySelector(".tab_label").innerText = "Non seguire";
								}
							}

							if (contentTypeId === "contrib.tag") {
								// correlato articolo
								if (document.querySelector("#_article_related_follow")) {
									document
										.querySelector("#_article_related_follow")
										.classList.add("active");
									document
										.querySelector("#_article_related_follow .tab_label")
										.querySelector(".tab_label").innerText = "Non seguire";
								}

								// pagina tag
								if (document.querySelector("#_tag_bookmarks")) {
									document
										.querySelector("#_tag_bookmarks")
										.classList.add("active");
									document
										.querySelector("#_tag_bookmarks .tab_label")
										.querySelector(".tab_label").innerText = "Non seguire";
								}

								// live seguita
								if (document.querySelector("#_live_follow")) {
									document
										.querySelector("#_live_follow")
										.classList.add("active");
									document
										.querySelector("#_live_follow .tab_label")
										.querySelector(".tab_label").innerText = "Non seguire";
								}
							}
						}
					})
					.catch((error) => {
						console.warn(" --- Error retrieving bookmark data: ", error);
					});
			}
		};

		// Retrieve planned lives
		const liveCalendar = async () => {
			const nodes = Array.from(
				document.querySelectorAll("._live_follow_single"),
			);
			await Promise.all(
				nodes.map(async (el) => {
					const bookmarkForm = el.querySelector("form");
					const contentTypeId =
						bookmarkForm.querySelector("input[name=model]").value;
					const objectId = bookmarkForm.querySelector(
						"input[name=object_id]",
					).value;
					const url = `/${["extra_bookmarks", contentTypeId, objectId].join("/")}`;

					await fetch(url, {
						headers: {
							"X-Requested-With": "XMLHttpRequest",
						},
						cache: "no-cache",
					})
						.then((response) => response.text())
						.then((data) => {
							if (data.indexOf("subscribed") >= 0) {
								el.classList.add("active");
								el.querySelector(".tab_label").innerText = "Non seguire";
							}
						})
						.catch((error) => {
							console.warn(
								" --- Error retrieving bookmark data for single live: ",
								error,
							);
						});
				}),
			);
		};

		// Retrieving comments
		const getComments = async () => {
			const commentsContainer = document.querySelector("#comments_container");
			if (commentsContainer) {
				import("../pages/include/comments.js").then(({ MpComments }) => {
					const comments = new MpComments();
					comments.loadComments();
				});
			}
		};

		// Reload iframe with poll embeds
		const pollEmbed = async () => {
			for (const el of document.querySelectorAll("._poll_embed")) {
				el.contentWindow.location.reload();
			}
		};

		// Retrieving gamecard vote
		const gamecardVote = async () => {
			const rating = document.querySelector("#_rating_form");
			if (!!rating && rating.dataset.id) {
				await fetch(`/misc/gamecard_vote/${rating.dataset.id}/`, {
					headers: {
						"X-Requested-With": "XMLHttpRequest",
					},
					cache: "no-cache",
				})
					.then((response) => response.json())
					.then((data) => {
						if (data.vote) {
							rating.querySelector('input[type="range"]').value = data.vote;
							rating.querySelector("output").textContent = data.vote;
						}
					})
					.catch((error) => {
						console.warn(" --- Error retrieving vote form:  ", error);
					});
			}
		};

		Promise.all([
			userNavLoad(),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#_article_related_follow"),
			),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#_article_read_later"),
			),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#_gamecard_follow"),
			),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#_tag_bookmarks"),
			),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#_live_follow"),
			),
			retrieveBookmarksDataFromBookmark(
				document.querySelector("#tab_follow_staff"),
			),
			liveCalendar(),
			getComments(),
			pollEmbed(),
			gamecardVote(),
		]).then(callback);
	}

	loginGAdataLayer() {
		updateGAdataLayer("login", "login", {
			method: "email",
		});
	}

	registrationGAdataLayer() {
		updateGAdataLayer("sign_up_requested", "sign_up", {
			method: "email",
			status: "requested",
		});
	}

	// User Nav
	userNavigationLogin(elements) {
		const userNavHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData();
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			element.addEventListener("click", userNavHandler);
			const removeLoginEvent = () => {
				element.removeEventListener("click", userNavHandler);
			};
			this.anonymousClicks.push(removeLoginEvent);
		}
	}

	// Staff report
	staffErrorReportLogin(element) {
		const staffReportHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData();
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};
		element.addEventListener("click", staffReportHandler);
		const removeLoginEvent = () => {
			element.removeEventListener("click", staffReportHandler);
		};
		this.anonymousClicks.push(removeLoginEvent);
	}

	hideAffiliationsLogin(element) {
		const hideAffiliationsHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					target.click();
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};
		element.addEventListener("click", hideAffiliationsHandler);
		const removeLoginEvent = () => {
			element.removeEventListener("click", hideAffiliationsHandler);
		};
		this.anonymousClicks.push(removeLoginEvent);
	}

	// Bookmarks
	bookmarkLogin(element) {
		const bookmarksHandler = (event) => {
			const target = event.currentTarget;

			// Preventing the action if is .no_action
			if (
				target.classList.contains("no_action") ||
				!!target.closest(".no_action")
			) {
				return;
			}

			event.preventDefault();
			event.stopImmediatePropagation();
			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					// After the login and after the user data is loaded we'll
					// fire a click only on not-already-active elements
					if (!target.classList.contains("active")) {
						const event = new Event("click");
						target.dispatchEvent(event);
						console.log(" --- Bookmark clicked");
					} else {
						console.log(" --- Bookmark not set: already active for the user");
					}
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};
		element.addEventListener("click", bookmarksHandler);
		const removeLoginEvent = () => {
			element.removeEventListener("click", bookmarksHandler);
		};
		this.anonymousClicks.push(removeLoginEvent);
	}

	// Error report form
	errorReportLogin(elements) {
		const errorReportHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					target.click();
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			element.addEventListener("click", errorReportHandler);
			const removeLoginEvent = () => {
				element.removeEventListener("click", errorReportHandler);
			};
			this.anonymousClicks.push(removeLoginEvent);
		}
	}

	// Comments
	commentVoteLogin(elements) {
		const commentVoteHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget ? event.currentTarget : event.target;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					if (
						!target.classList.contains("active") &&
						!target.classList.contains("disabled") &&
						!!document.querySelector(`#${target.id}`)
					) {
						document.querySelector(`#${target.id}`).click();
					}
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			if (!element.dataset.loginHandler) {
				element.addEventListener("click", commentVoteHandler);
				const removeLoginEvent = () => {
					element.removeEventListener("click", commentVoteHandler);
					element.setAttribute("data-login-handler", 0);
				};
				element.setAttribute("data-login-handler", 1);
				this.anonymousClicks.push(removeLoginEvent);
			}
		}
	}

	commentTextareaLogin(elements) {
		const commentTextareaHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData();
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				for (const el of document.querySelectorAll("button._comment_submit")) {
					el.setAttribute("disabled", true);
				}
			};
		};

		for (const element of elements) {
			element.addEventListener("focus", commentTextareaHandler);
			const removeLoginEvent = () => {
				element.removeEventListener("focus", commentTextareaHandler);
			};
			this.anonymousFocuses.push(removeLoginEvent);
		}
	}

	commentAnswerLogin(elements) {
		const commentAnswerHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					if (document.querySelector(`#${target.id}`)) {
						document.querySelector(`#${target.id}`).click();
					}
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			if (!element.dataset.loginHandler) {
				element.addEventListener("click", commentAnswerHandler);
				const removeLoginEvent = () => {
					element.removeEventListener("click", commentAnswerHandler);
					element.setAttribute("data-login-handler", 0);
				};
				element.setAttribute("data-login-handler", 1);
				this.anonymousClicks.push(removeLoginEvent);
			}
		}
	}

	// Polls
	pollLogin(elements) {
		const pollHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					const form = target.closest("form");
					if (target.classList.contains("_poll_index")) {
						ajaxSurvey(form);
					} else {
						formAddCsrfProtection(form).then((data) => {
							form.submit();
						});
					}
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			element.addEventListener("click", pollHandler);
			const removeLoginEvent = () => {
				element.removeEventListener("click", pollHandler);
			};
			this.anonymousClicks.push(removeLoginEvent);
		}
	}

	// Paywall
	paywallLogin(elements) {
		const paywallHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData(() => {
					target.click();
				});
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};

		for (const element of elements) {
			element.addEventListener("click", paywallHandler);
			const removeLoginEvent = () => {
				element.removeEventListener("click", paywallHandler);
			};
			this.anonymousClicks.push(removeLoginEvent);
		}
	}

	clickioPaywallLogin(element) {
		const clickioPaywallHandler = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			const target = event.currentTarget;

			const cmpContainer = document.querySelector("#cl-consent");
			cmpContainer.style.zIndex = 1;

			this.openLoginWindow();
			this.delegate = target;

			// Delegate function
			target.authenticationSucceded = () => {
				this.removeEventHandlers();
				this.getUserData();
				cmpContainer.style.zIndex = "";
			};

			// Delegate function
			target.authenticationFailedWithError = (error) => {
				//console.warn(error)
				return;
			};
		};
		element.addEventListener("click", clickioPaywallHandler);
		const removeLoginEvent = () => {
			element.removeEventListener("click", clickioPaywallHandler);
		};
		this.anonymousClicks.push(removeLoginEvent);
	}
}

window.globalLoginWindowHandler = new LoginWindowHandler();

/* On document ready */
function ready(fn) {
	if (document.readyState !== "loading") {
		fn();
	} else {
		document.addEventListener("DOMContentLoaded", fn);
	}
}

ready(() => {
	if (document.querySelector("#login_registration_container")) {
		/* Privacy */
		document
			.querySelector("#registration_window #normativa")
			.addEventListener("click", () => {
				const w = 600;
				const h = 500;
				const left = screen.width / 2 - w / 2;
				const top = screen.height / 2 - h / 2;
				window.open(
					"/privacy-policy/?reg",
					"Condizioni sulla Privacy",
					`toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`,
				);
			});

		let loginWindowErrorTimer;

		/* Login */
		document
			.querySelector(
				'#login_window form.multiplayer_login input[type="button"]',
			)
			.addEventListener("click", (event) => {
				event.preventDefault();

				const showErrorWithText = (errorText) => {
					if (typeof loginWindowErrorTimer !== "undefined") {
						clearTimeout(loginWindowErrorTimer);
					}
					const errorList = document.querySelector("#login_window .error_list");
					errorList.classList.add("alert", "alert-danger");
					errorList.querySelector("div").textContent = errorText;
					errorList.classList.remove("d-none");
					loginWindowErrorTimer = window.setTimeout(() => {
						errorList.classList.add("d-none");
					}, 3000);
				};

				const button = event.currentTarget;
				button.setAttribute("disabled", "disabled");
				const form = document.querySelector(
					"#login_window form.multiplayer_login",
				);

				formAddCsrfProtection(form).then((data) => {
					const formData = new FormData(form);
					fetch(form.getAttribute("action"), {
						headers: {
							"X-Requested-With": "XMLHttpRequest",
						},
						method: "POST",
						body: formData,
					})
						.then((response) => response.json())
						.then((data) => {
							if (!data.authenticated) {
								window.globalLoginWindowHandler.loginSucceded = false;
								showErrorWithText("Email o password errata");
							} else {
								window.globalLoginWindowHandler.loginSucceded = true;
								window.globalLoginWindowHandler.loginLightbox.close();
								window.globalLoginWindowHandler.loginGAdataLayer();
								window.globalLoginWindowHandler.delegate.authenticationSucceded();
							}
							button.removeAttribute("disabled");
						})
						.catch((error) => {
							showErrorWithText("Errore di connessione");
							button.removeAttribute("disabled");
						});
				});
			});

		/* Registration */
		document
			.querySelector(
				'#registration_window form.multiplayer_login input[type="button"]',
			)
			.addEventListener("click", (event) => {
				event.preventDefault();

				const showErrorWithText = (errorText) => {
					if (typeof loginWindowErrorTimer !== "undefined") {
						clearTimeout(loginWindowErrorTimer);
					}
					const errorList = document.querySelector(
						"#registration_window .error_list",
					);
					errorList.classList.add("alert", "alert-danger");
					errorList.querySelector("div").textContent = errorText;
					errorList.classList.remove("d-none");
					loginWindowErrorTimer = window.setTimeout(() => {
						errorList.classList.add("d-none");
					}, 3000);
				};

				const button = event.currentTarget;
				button.setAttribute("disabled", "disabled");
				const form = document.querySelector(
					"#registration_window form.multiplayer_login",
				);

				formAddCsrfProtection(form).then((data) => {
					const formData = new FormData(form);
					fetch(form.getAttribute("action"), {
						headers: {
							"X-Requested-With": "XMLHttpRequest",
						},
						method: "POST",
						body: formData,
					})
						.then((response) => response.json())
						.then((data) => {
							if (data.success) {
								form.parentNode.removeChild(form);
								document
									.querySelector("#registration_window .registration_completed")
									.classList.remove("d-none");
								window.globalLoginWindowHandler.registrationGAdataLayer();
							} else {
								window.globalLoginWindowHandler.loginSucceded = false;
								for (const e in data.errors) {
									showErrorWithText(data.errors[e][0]);
									break;
								}
								if (data.captcha_error) {
									showErrorWithText(data.captcha_error);
								}
							}
							button.removeAttribute("disabled");
						})
						.catch((error) => {
							showErrorWithText("Errore di connessione");
							button.removeAttribute("disabled");
						});
				});
			});

		// Switch login/registration
		document
			.querySelector("#login_window a.register")
			.addEventListener("click", (e) => {
				e.preventDefault();
				// CAPTCHA per la registrazione
				loadScript(
					"_recaptcha-script",
					"https://www.google.com/recaptcha/api.js?hl=it",
				).then(() => {
					document.querySelector("#login_window").classList.add("d-none");
					document
						.querySelector("#registration_window")
						.classList.remove("d-none");
					document.querySelector("#registration_window .email").focus();
				});
			});

		document
			.querySelector("#registration_window a.login")
			.addEventListener("click", (e) => {
				e.preventDefault();
				document.querySelector("#login_window").classList.remove("d-none");
				document.querySelector("#registration_window").classList.add("d-none");
				document.querySelector("#login_window .username").focus();
			});
	}
});
